import React, { useState } from 'react';
import { useMood } from '../../contexts/MoodContext';
import { getMood } from './moods';
import TodayView from './TodayView';
import WeeklyView from './WeeklyView';
import MonthlyView from './MonthlyView';
import AmbientBackground from './visual/AmbientBackground';
import FloatingParticleLayer from './visual/FloatingParticleLayer';
import './MoodMate.css';
import './MoodJournalViews.css';
import './MoodWorld.css';

type Tab = 'today' | 'weekly' | 'monthly';

/**
 * MoodJournalTabs — the Today / Weekly / Monthly journal body.
 *
 * Self-contained (depends only on useMood), so it can be dropped into any
 * container: the My Information "Mood Journal" section hosts it, and it owns its
 * own tab state, mood theming, and loading/offline strips. Each view computes its
 * numbers from real stored check-ins only.
 */
export const MoodJournalTabs: React.FC = () => {
  const { sessionMood, status } = useMood();
  const [tab, setTab] = useState<Tab>('today');
  const mood = getMood(sessionMood);

  return (
    <div
      className="mm-journal-tabs mm-journal-tabs--immersive"
      style={mood ? { ['--mood-accent' as string]: mood.accent, ['--mood-soft' as string]: mood.soft } : undefined}
    >
      <AmbientBackground waves={false} />
      <FloatingParticleLayer count={8} />
      <div className="mm-journal-tabs__inner">
      {status.loading && <p className="mm-settings__status" role="status">Loading your mood history…</p>}
      {!status.loading && status.offline && (
        <p className="mm-settings__status" role="status">
          You’re offline — showing what’s saved on this device. Changes sync when you reconnect.
        </p>
      )}

      <div className="mm-tabs" role="tablist" aria-label="Mood journal views">
        {(['today', 'weekly', 'monthly'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={`mm-tab ${tab === t ? 'is-active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'today' ? 'Today' : t === 'weekly' ? 'Weekly' : 'Monthly'}
          </button>
        ))}
      </div>

      {tab === 'today' && <TodayView />}
      {tab === 'weekly' && <WeeklyView />}
      {tab === 'monthly' && <MonthlyView />}
      </div>
    </div>
  );
};

export default MoodJournalTabs;
